import { fetchAuthSession } from 'aws-amplify/auth';
import type { ApiErrorBody, ErrorCode } from '@dubforge/shared';
import { i18n } from '../i18n';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: ErrorCode,
  ) {
    super(message);
  }
}

/** Calls the DubForge API with the signed-in user's ID token (refreshed automatically by Amplify). */
export async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new Error(i18n.t('common.networkError'));
  }
  if (!res.ok) {
    const data = (await res.json().catch(() => null)) as Partial<ApiErrorBody> | null;
    throw new ApiError(res.status, data?.error ?? `Request failed (${res.status})`, data?.code);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** A message for the user, in their language when the API gave a known error code. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    // These come with specific details (mostly on admin screens) that the generic text would lose.
    if (err.code && ['conflict', 'invalid_request', 'discord_unreachable'].includes(err.code)) return err.message;
    return err.code ? i18n.t(`errors.${err.code}`) : i18n.t('common.genericError');
  }
  if (err instanceof Error && err.message) return err.message;
  return i18n.t('common.genericError');
}
